// src/api/jobs.js
// 说明：前端叫 jobs，但实际操作 PB 的 runs 集合（PocketBase SDK）
//
// 依赖：你项目里已有的 pb 实例（通常是 export const pb = new PocketBase(...)）
// 路径按你的实际项目调整：@/utils/pocketbase
import { pb } from "@/utils/pocketbase";
import { safePb } from "@/utils/pocketbase-safe";
/**
 * List jobs (runs)
 * @param {Object} query
 * @param {number} query.page      1-based
 * @param {number} query.perPage   page size
 * @param {string} query.keyword   search by jobName / id / etc
 * @param {string} query.status    queued/running/finished/failed/...
 * @param {string} query.owner     userId (optional)
 */
export const listJobs = async (query = {}) => {
    const page = Number(query.page || 1);
    const perPage = Number(query.perPage || 10);

    // 你 runs 里如果有 job_name 字段，就用 job_name；没有就删掉这条 filter
    const keyword = String(query.keyword || "").trim();
    const status = String(query.status || "").trim();
    const owner = String(query.owner || "").trim();

    const filters = [];

    if (status) filters.push(`status = "${escapeFilterValue(status)}"`);

    if (owner) filters.push(`owner = "${escapeFilterValue(owner)}"`);

    // 这里假设你 runs 有 job_name 字段；如果没有，改成你实际字段（例如 name）
    if (keyword) {
        // 多字段模糊：job_name / id / cromwell_workflow_id（按你实际字段调整）
        const k = escapeFilterValue(keyword);
        filters.push(`(job_name ~ "${k}" || id ~ "${k}" || cromwell_workflow_id ~ "${k}")`);
    }

    const filter = filters.length ? filters.join(" && ") : "";

    // expand：把 algorithm 关联记录展开，方便直接显示算法名
    // sort：按提交时间倒序（你字段如果叫 submitted_at）
    const res = await pb.collection("runs").getList(page, perPage, {
        filter,
        expand: "algorithm",
        sort: "-submitted_at",
    });

    // 你页面里原来叫 fileList/total，这里给你 normalize 成你想要的结构也行
    // 我这里直接返回 PB 原始结构（page/perPage/totalItems/items）
    return res;
};

/**
 * Get job detail (run)
 * @param {string} jobId
 */
export const getJobDetail = async (jobId) => {
    // expand 需要什么你就加什么：owner、algorithm 等
    return pb.collection("runs").getOne(jobId, {
        expand: "algorithm,owner",
    });
};

/**
 * Download result artifact
 * 前提：runs.result_files 已经改为 file field（单文件或多文件都可）
 * @param {string} jobId
 * @returns {Promise<{blob: Blob, filename: string}>}
 */
export const getDownloadStream = async (jobId) => {
    const rec = await pb.collection("runs").getOne(jobId);

    const rf = rec.result_files;
    if (!rf || (Array.isArray(rf) && rf.length === 0)) {
        throw new Error("No result_files on this run.");
    }

    // 如果是多文件字段，你可以按需选择第一个或让调用方传 filename
    const filename = Array.isArray(rf) ? rf[0] : rf;

    // PocketBase file URL（注意：会包含 token / thumb 等参数，你直接 fetch 即可）
    const url = pb.files.getUrl(rec, filename);

    const resp = await fetch(url, {
        method: "GET",
        // 如果你的 PB 需要 auth 才能下载，PB SDK 已维护 authStore，
        // fetch 默认会带 cookie（同域）；跨域如果 token 在 url 里也可以
    });
    if (!resp.ok) {
        throw new Error(`Download failed: HTTP ${resp.status}`);
    }
    const blob = await resp.blob();

    return { blob, filename };
};

/**
 * Cancel job
 * 说明：如果你是“PB hooks / 后端 worker”监听 status 来取消 Cromwell，
 * 那么前端这里只要把 status 改成 canceled 即可。
 * @param {string} jobId
 */
export const cancelJob = async (jobId) => {
    return safePb.collection("runs").update(jobId, {
        status: "canceled",
    });
};

/**
 * Delete/purge job record
 * @param {string} jobId
 */
export const purgeJobRecord = async (jobId) => {
    return safePb.collection("runs").delete(jobId);
};

// --------------------
// utils
// --------------------
function escapeFilterValue(v) {
    // PB filter 字符串需要把 " 和 \ 做转义
    return String(v).replace(/\\/g, "\\\\").replace(/"/g, '\\"');
}